import { isAllowedMaintenanceUrl } from './api'
import { isNoticeDismissed } from './dismissal'
import { MaintenanceScreen, NoticeConfig } from './types'

/**
 * 점 구분 버전 비교(3~4 segment 허용). semver 패키지 미사용 경량 구현.
 * @returns a<b → -1, a===b → 0, a>b → 1
 */
export function compareVersion(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0)
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i += 1) {
    const da = pa[i] ?? 0
    const db = pb[i] ?? 0
    if (da !== db) return da < db ? -1 : 1
  }
  return 0
}

/** now(ms) 가 [start, end) 구간(ISO8601 UTC)에 속하는지. */
function isWithinPeriod(now: number, start: string, end: string): boolean {
  const s = Date.parse(start)
  const e = Date.parse(end)
  if (Number.isNaN(s) || Number.isNaN(e)) return false
  return now >= s && now < e
}

function hasWebScope(scope: string[]): boolean {
  return scope.includes('all') || scope.includes('web')
}

interface EvaluateInput {
  config: NoticeConfig | null
  currentVersion: string
  now: Date
}

/**
 * notice.json 을 평가해 표시할 화면 1건을 결정한다.
 * - 트랙 A(점검): maintenance.active && scope ∋ all|web 일 때만 blackout/notice 평가.
 * - 트랙 B(강제 업데이트): active 와 무관하게 force_update.web 버전 비교.
 * - 우선순위: blackout > forceUpdate > notice.
 */
export function evaluateMaintenance({
  config,
  currentVersion,
  now,
}: EvaluateInput): MaintenanceScreen {
  if (!config) return { kind: 'none' }

  const nowMs = now.getTime()

  // 트랙 A: 점검(maintenance)
  const maintenance = config.maintenance
  const maintenanceActive = !!maintenance && maintenance.active && hasWebScope(maintenance.scope)

  if (maintenanceActive && maintenance) {
    const blackout = maintenance.blackout_period
    if (
      blackout &&
      isAllowedMaintenanceUrl(blackout.url) &&
      isWithinPeriod(nowMs, blackout.start, blackout.end)
    ) {
      return { kind: 'blackout', url: blackout.url, end: blackout.end }
    }
  }

  // 트랙 B: 강제 업데이트(force_update) — active 무관
  const forceUpdate = config.force_update
  const minVersion = forceUpdate?.web?.min_version
  if (
    forceUpdate &&
    minVersion &&
    isAllowedMaintenanceUrl(forceUpdate.url) &&
    compareVersion(currentVersion, minVersion) < 0
  ) {
    return { kind: 'forceUpdate', url: forceUpdate.url }
  }

  // 트랙 A: 사전 공지(notice) — 우선순위상 가장 약함
  if (maintenanceActive && maintenance) {
    const notice = maintenance.notice_period
    if (
      notice &&
      isAllowedMaintenanceUrl(notice.url) &&
      isWithinPeriod(nowMs, notice.start, notice.end) &&
      !isNoticeDismissed(notice.modified_at, now)
    ) {
      return { kind: 'notice', url: notice.url, modifiedAt: notice.modified_at }
    }
  }

  return { kind: 'none' }
}
